import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import clsx from "clsx";
import React from "react";
import type { DraggableProvided } from "react-beautiful-dnd";
import { Draggable } from "react-beautiful-dnd";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import type { NskResponse } from "~/models/Api";
import type {
	DealSettlement,
	DealSettlementEdit
} from "~/models/DealSettlement";
import { isSuccess } from "~/utils/api/api_handler";
import { toThousandSeparator } from "~/utils/CurrencySeparator";
import { SettlementEditModal } from "./SettlementEditModal";
import Styles from "./SettlementList.module.scss";

type Props = {
	dealSettlement: DealSettlement;
	handleDestroy: (deleteSettlement: DealSettlement) => Promise<NskResponse>;
	handleMutateSettlements: () => Promise<void>;
	handleUpdate: (
		submitValue: DealSettlementEdit,
		billingId: number
	) => Promise<NskResponse>;
	index: number;
	onToggleSelect: (id: number) => void;
	readonly: boolean;
	selectedIds: readonly number[];
	target: "owner" | "rentee";
};

export function SettlementListRow({
	dealSettlement,
	handleMutateSettlements,
	handleUpdate,
	handleDestroy,
	index,
	selectedIds,
	onToggleSelect,
	readonly,
	target
}: Props) {
	const { notify } = useNotification();
	const { ownerAmount, renteeAmount } = dealSettlement;
	const [showDestroyConfirm, setShowDestroyConfirm] =
		React.useState<boolean>(false);
	const [showEdit, setShowEdit] = React.useState<boolean>(false);
	const onClickEdit = (e: React.MouseEvent): void => {
		e.preventDefault();
		setShowEdit(true);
	};
	const onClickDelete = (e: React.MouseEvent): void => {
		e.preventDefault();
		setShowDestroyConfirm(true);
	};
	const handleDeleteSettlement = async (): Promise<void> => {
		const result = await handleDestroy(dealSettlement);
		if (isSuccess(result)) {
			await handleMutateSettlements();
			notify({ content: "精算項目を削除しました。", variant: "success" });
			setShowDestroyConfirm(false);
			return;
		}
		notify({ content: "エラーが発生しました。", variant: "error" });
	};
	return (
		<Draggable
			draggableId={String(dealSettlement.id)}
			index={index}
			isDragDisabled={readonly}
			key={dealSettlement.id}
		>
			{(provided: DraggableProvided): React.ReactElement<HTMLElement> => (
				<tr
					className={clsx(Styles.ListRow, Styles.Enabled)}
					ref={provided.innerRef}
					style={provided.draggableProps.style}
					{...provided.draggableProps}
				>
					<td className={Styles.Select}>
						<InputCheckBox
							checked={selectedIds.includes(dealSettlement.id)}
							disabled={readonly}
							onChange={(): void => {
								onToggleSelect(dealSettlement.id);
							}}
						/>
					</td>
					<td className={Styles.OrderHandle} {...provided.dragHandleProps}>
						{!readonly && (
							<img alt="drag handle" src="/assets/DragHandle.svg" />
						)}
					</td>
					<td className={Styles.Settlement}>
						{dealSettlement.name}
						<div className={Styles.SPInformation}>
							税率：
							{dealSettlement.taxRate}
						</div>
						{dealSettlement.note !== null && dealSettlement.note !== "" && (
							<div className={Styles.Note}>備考）{dealSettlement.note}</div>
						)}
					</td>
					<td className={Styles.NonTaxAmount}>
						<span>
							¥
							{toThousandSeparator(
								target === "owner" ? ownerAmount : renteeAmount
							)}
						</span>
					</td>
					<td className={Styles.TaxRate}>
						<div>{dealSettlement.taxRate}</div>
					</td>
					<td className={Styles.TaxAmount}>
						<span>¥{toThousandSeparator(dealSettlement.amountWithTax)}</span>
					</td>
					<td className={Styles.Edit}>
						{!readonly && (
							<>
								<a className={Styles.EditLink} href="#" onClick={onClickEdit}>
									編集
								</a>
								<a
									className={Styles.DeleteLink}
									href="#"
									onClick={onClickDelete}
								>
									削除
								</a>
							</>
						)}
						{showDestroyConfirm ? (
							<ConfirmModal
								confirmType="delete"
								handleAccept={handleDeleteSettlement}
								handleCancel={(): void => {
									setShowDestroyConfirm(false);
								}}
								isOpen={showDestroyConfirm}
							>
								選択した精算項目を削除します。
							</ConfirmModal>
						) : null}
						{showEdit ? (
							<SettlementEditModal
								dealSettlement={dealSettlement}
								handleClose={(): void => {
									setShowEdit(false);
								}}
								handleMutateSettlements={handleMutateSettlements}
								handleUpdate={handleUpdate}
							/>
						) : null}
					</td>
				</tr>
			)}
		</Draggable>
	);
}
